import { apiClient } from 'axios-client';
import FormField from 'components/form-field/form-field';
import { Formik } from 'formik';
import { useRefuseReview } from 'generated/api-client/@tanstack/react-query.gen';
import { useCurrentBilikPerson } from 'hooks/use-current-bilik-person/use-current-bilik-person';
import { useReviewLocker } from '../hooks/useReviewLocker';
import React, { FunctionComponent, useCallback } from 'react';
import { Button, Form, Icon, Message } from 'semantic-ui-react';
import { PhoneNumber, ToastError, ToastSuccess, ToastWarn } from 'utils';
import * as Yup from 'yup';

type RefuseReviewFormProps = {
  reviewId: number;
  onCompleted: () => void;
};

export const RefuseReviewForm: FunctionComponent<RefuseReviewFormProps> = ({
  reviewId,
  onCompleted,
}) => {
  const { currentBilikPerson } = useCurrentBilikPerson();

  const { unlock, checkLocker } = useReviewLocker(reviewId);

  const { mutateAsync: refuse } = useRefuseReview();

  const refuseReview = useCallback(
    async (reason: string) => {
      try {
        const locker = await checkLocker();
        if (locker && locker.id !== currentBilikPerson?.id) {
          ToastError(
            'Erreur',
            `Avis verrouillé par ${locker.givenName} ${locker.familyName}, impossible de le refuser.`,
          );
          return;
        }

        await unlock();

        // V2
        await refuse({
          path: { reviewId },
          body: { reason },
        });

        const response = await apiClient.post(`/form/review-pro/refuse`, {
          proReviewId: reviewId,
          reason,
        });

        response.data.success === true
          ? ToastSuccess('Succès', 'Avis refusé.')
          : ToastWarn('Attention', response.data.message);
      } catch (e) {
        ToastError('Erreur', "Impossible de refuser l'avis.");
      }

      onCompleted();
    },
    [
      reviewId,
      unlock,
      checkLocker,
      currentBilikPerson?.id,
      refuse,
      onCompleted,
    ],
  );

  return (
    <Formik
      validationSchema={Yup.object().shape({
        refuseReason: Yup.string().required('Réponse requise !'),
      })}
      validateOnChange={true}
      initialValues={{ refuseReason: '' }}
      onSubmit={async (values, actions): Promise<void> => {
        await refuseReview(values.refuseReason);
        actions.setSubmitting(false);
      }}
    >
      {(props): React.ReactElement => (
        <Form onReset={props.handleReset} onSubmit={props.handleSubmit}>
          <FormField
            type="textarea"
            placeholder="Raison du refus"
            label="Raison du refus"
            helpText="Ne pas signer ! La signature est automatique. La raison est transmise au particulier et au pro"
            name="refuseReason"
            required
          />
          {currentBilikPerson && (
            <Message>
              <p>Cordialement,</p>
              <p>
                {currentBilikPerson.givenName} {currentBilikPerson.familyName} -
                Bilik
                <br />
                <a href={`tel:${currentBilikPerson.telephone}`}>
                  {PhoneNumber.format(currentBilikPerson.telephone ?? 'error')}
                </a>
              </p>
            </Message>
          )}
          <Button
            primary
            type="submit"
            loading={props.isSubmitting}
            disabled={props.isSubmitting}
          >
            <Icon name="mail" />
            Envoyer
          </Button>
        </Form>
      )}
    </Formik>
  );
};
